// Public PAGE interview (NO AUTH). Tim/Curtis open this on their phone and
// answer questions about an existing website page; the answers are turned into
// humanized copy + attributed pull-quotes for that page.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getPageInterviewByToken,
  savePageInterviewAnswer,
  completePageInterview,
} from "@/lib/page-interview.functions";
import { VoiceRecorder } from "@/components/interview/VoiceRecorder";
import logoAsset from "@/assets/durafoam-logo.webp.asset.json";
import { ArrowLeft } from "lucide-react";

function normalizeToken(token: string) {
  try {
    return decodeURIComponent(token).trim();
  } catch {
    return token.trim();
  }
}

function isUsableToken(token: string) {
  const normalized = normalizeToken(token);
  const lower = normalized.toLowerCase();
  if (["<token>", "{token}", ":token", "$token", "token"].includes(lower)) return false;
  return /^[a-z0-9_-]{8,200}$/i.test(normalized);
}

export const Route = createFileRoute("/page-interview/$token")({
  ssr: false,
  loader: async ({ params }) => {
    const token = normalizeToken(params.token);
    if (!isUsableToken(token)) throw notFound();
    const res = await getPageInterviewByToken({ data: { token } });
    if (!res) throw notFound();
    return res;
  },
  head: () => ({
    meta: [
      { title: "Durafoam Roofing — Page Interview" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
    ],
  }),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <p className="text-sm text-slate-600">Could not load this interview. {error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="text-xl font-semibold text-[#0F1A42]">Link not found</h1>
        <p className="mt-2 text-sm text-slate-600">
          This interview link is invalid, missing its unique token, or has already been used.
        </p>
      </div>
    </div>
  ),
  component: PageInterviewPage,
});

type Question = { id: string; question_text: string; order: number };

function PageInterviewPage() {
  const data = Route.useLoaderData();
  const { token: tokenParam } = Route.useParams();
  const token = normalizeToken(tokenParam);
  const session = data.session;
  const questions: Question[] = ((session.questions ?? []) as Question[])
    .slice()
    .sort((a: Question, b: Question) => a.order - b.order);


  const initialAnswers: Record<string, string> = {};
  const initialNotes: Record<string, string> = {};
  for (const a of data.answers) {
    initialAnswers[a.question_id] = a.transcript;
    initialNotes[a.question_id] = (a as any).clarification ?? "";
  }

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(
    session.status === "completed" ||
      session.status === "reviewed" ||
      session.status === "awaiting_ai_draft" ||
      session.status === "pending_approval",
  );
  const [idx, setIdx] = useState(() => {
    const i = questions.findIndex((q) => !initialAnswers[q.id]);
    return i === -1 ? 0 : i;
  });
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);
  const [notes, setNotes] = useState<Record<string, string>>(initialNotes);
  const [audio, setAudio] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);

  const save = useServerFn(savePageInterviewAnswer);
  const complete = useServerFn(completePageInterview);

  if (completed) return <DoneScreen />;

  const answeredCount = questions.filter((qq) => (answers[qq.id] ?? "").trim()).length;

  if (!started) {
    const resuming = answeredCount > 0;
    return (
      <Shell>
        <h1 className="mt-6 text-2xl font-bold text-[#0F1A42]">
          {resuming ? "Pick up where you left off" : "Quick interview about one of our pages"}
        </h1>
        <p className="mt-2 text-base text-slate-600">{session.page_title}</p>
        <p className="mt-6 text-[15px] leading-relaxed text-slate-700">
          {resuming
            ? `You've answered ${answeredCount} of ${questions.length} so far. Your answers are saved — just keep going.`
            : `We'll ask you ${questions.length} question${questions.length === 1 ? "" : "s"}. Tap the microphone and answer naturally — like you're explaining it to a customer standing on the driveway. You can stop any time and come back to this same link later.`}
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
          Your answers get used to rewrite the page in your own words, with a few direct quotes from
          you. Nothing goes live until it's reviewed.
        </p>
        <button
          onClick={() => setStarted(true)}
          className="mt-8 w-full rounded-xl bg-[#D97706] py-4 text-lg font-semibold text-white shadow-sm active:translate-y-px"
        >
          {resuming ? "Continue interview" : "Start interview"}
        </button>
      </Shell>
    );
  }

  const q = questions[idx];
  const total = questions.length;

  async function persist(opts: { skipped?: boolean } = {}) {
    await save({
      data: {
        token,
        question_id: q.id,
        question_text: q.question_text,
        transcript: (answers[q.id] ?? "").trim(),
        clarification: (notes[q.id] ?? "").trim(),
        skipped: !!opts.skipped,
        audio_url: audio[q.id] ?? null,

      },
    });
  }

  return (
    <Shell>
      <div className="mt-4 flex items-center justify-between text-xs font-medium text-slate-500">
        <button
          type="button"
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className="inline-flex items-center gap-1 disabled:opacity-30"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <span>
          Question {idx + 1} of {total}
        </span>
        <span />
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#D97706] transition-all"
          style={{ width: `${((idx + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className="mt-8 text-xl font-semibold leading-snug text-[#0F1A42]">{q.question_text}</h2>

      <VoiceRecorder
        key={q.id}
        token={token}
        initialTranscript={answers[q.id] ?? ""}
        onTranscript={(t) => setAnswers((prev) => ({ ...prev, [q.id]: t }))}
        onAudioUrl={(url) => setAudio((prev) => ({ ...prev, [q.id]: url }))}
      />



      <div className="mt-6">
        <label
          htmlFor={`note-${q.id}`}
          className="text-xs font-semibold uppercase tracking-wide text-slate-500"
        >
          Anything to add in writing? (optional)
        </label>
        <textarea
          id={`note-${q.id}`}
          value={notes[q.id] ?? ""}
          onChange={(e) => setNotes((prev) => ({ ...prev, [q.id]: e.target.value }))}
          rows={3}
          placeholder="A name, a date, a number — anything easier to type than say."
          className="mt-2 w-full rounded-xl border border-slate-200 bg-white p-3 text-base text-[#0F1A42] focus:border-[#D97706] focus:outline-none"
        />
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          disabled={(!answers[q.id]?.trim() && !notes[q.id]?.trim()) || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await persist();
              if (idx + 1 >= total) {
                await complete({ data: { token } });
                setCompleted(true);
              } else {
                setIdx(idx + 1);
              }
            } finally {
              setSaving(false);
            }
          }}
          className="w-full rounded-xl bg-[#D97706] py-4 text-lg font-semibold text-white shadow-sm disabled:opacity-40 active:translate-y-px"
        >
          {saving ? "Saving…" : idx + 1 >= total ? "Finish interview" : "Next question →"}
        </button>
        {idx + 1 < total ? (
          <button
            type="button"
            onClick={async () => {
              setSaving(true);
              try {
                await persist({ skipped: true });
              } finally {
                setSaving(false);
                setIdx(idx + 1);
              }
            }}
            className="text-sm font-medium text-slate-500 underline"
          >
            Skip this one
          </button>
        ) : null}
        {answeredCount > 0 && idx + 1 < total ? (
          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                if ((answers[q.id] ?? "").trim() || (notes[q.id] ?? "").trim()) await persist();
                await complete({ data: { token } });
                setCompleted(true);
              } finally {
                setSaving(false);
              }
            }}
            className="text-sm font-medium text-slate-500 underline"
          >
            Submit what I have and finish
          </button>
        ) : null}
        <p className="text-center text-xs text-slate-400">
          Your answers save as you go — you can close this and come back to the same link.
        </p>
      </div>
    </Shell>
  );
}


function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto w-full max-w-xl px-5 pb-16 pt-8">
        <img src={logoAsset.url} alt="Durafoam Roofing" width={40} height={40} className="h-10 w-auto" />
        {children}
      </div>
    </div>
  );
}

function DoneScreen() {
  return (
    <Shell>
      <div className="mt-16 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#D97706]/10 text-3xl">
          ✓
        </div>
        <h1 className="mt-6 text-2xl font-bold text-[#0F1A42]">All done — thank you!</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
          We're rewriting the page with your answers now. The team reviews every section before
          anything goes live.
        </p>
      </div>
    </Shell>
  );
}
